import React, {useEffect, useState} from "react";
import {SubclassDisplayChart} from "./components/SubclassDisplayChart";
import {useTranslation} from "react-i18next";
import axiosInstance from "../../api/axios";

// Helper function to safely convert to ISO string
const toISOString = (date) => {
  if (!date) return null;
  try {
    if (typeof date === 'string') {
      const parsed = new Date(date);
      if (isNaN(parsed.getTime())) return null;
      return parsed.toISOString();
    }
    if (date instanceof Date) {
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    }
    return null;
  } catch (error) {
    console.error('Error converting to ISO string:', error);
    return null;
  }
};

const SubclassDisplay = ({
  id,
  currentWasteItem,
  dayvalue,
  language,
  message2,
  selectedButton,
  totalwasteproduced,
  colorwaste,
  percentagechange,
  TotalWasteWeight,
  datevalue,
  currentWasteItem_,
  wasteweight: wasteweightProp,
  isCustomDateRange
}) => {
  const { t, i18n } = useTranslation();
  const [totalwasteproduceddata, setTotalWasteProducedData] = useState(0);
  const [wasteweight, setWasteWeight] = useState(0);
  const [colorwastechart, setColorWasteChart] = useState('gray');
  const [percentagechangewastechart, setPercentageChangeWasteChart] = useState(0);
  const [season, setSeason] = useState('summer');

  const getCurrentSeason = () => {
    const month = new Date().getMonth() + 1;
    if (month >= 3 && month <= 5) return 'spring';
    if (month >= 6 && month <= 8) return 'summer';
    if (month >= 9 && month <= 11) return 'autumn';
    return 'winter';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const binIdentifiers = Array.isArray(id) ? id : [id];
        const bins = binIdentifiers.join(',');

        const timeRangeMap = {
          'day': 'Day',
          'week': 'Week',
          'month': 'Month',
          '3months': '3Month',
          '3 month': '3Month',
          '3-month': '3Month',
          '6months': '6Month',
          '6 month': '6Month',
          '6-month': '6Month',
          'year': 'Year'
        };

        const normalizedDayValue = dayvalue.toLowerCase().replace(/\s+/g, '').replace(/-/g, '');
        const timeRange = timeRangeMap[normalizedDayValue] || timeRangeMap[dayvalue] || 'Month';
        const currentSeason = getCurrentSeason();

        const hasCustomDates = isCustomDateRange && 
                               datevalue && 
                               datevalue[0] && 
                               datevalue[0].startDate && 
                               datevalue[0].endDate;

        console.log('SubclassDisplay: API Request Params:', {
          bins,
          timeRange,
          season: currentSeason,
          originalDayValue: dayvalue,
          normalizedDayValue,
          isCustomDateRange,
          hasCustomDates,
          dateRangeValue: datevalue
        });

        // Build params for composition API
        const compositionParams = {
          bins: bins,
          timeRange: timeRange,
          season: currentSeason
        };

        // Add custom date range if applicable - SAFE HANDLING
        if (hasCustomDates) {
          const startISO = toISOString(datevalue[0].startDate);
          const endISO = toISOString(datevalue[0].endDate);
          
          if (startISO && endISO) {
            compositionParams.start_date = startISO; // Just the date part
            compositionParams.end_date = endISO;
            console.log('SubclassDisplay: Using custom date range:', {
              start_date: compositionParams.start_date,
              end_date: compositionParams.end_date
            });
          }
        }

        // Fetch composition data
        const compositionResponse = await axiosInstance.get('/others_page_waste_composition', {
          params: compositionParams
        });

        console.log('SubclassDisplay: Composition API Response:', compositionResponse.data);

        if (compositionResponse.data && compositionResponse.data.total !== undefined) {
          setWasteWeight(Number(compositionResponse.data.total.toFixed(2)));
        }

        // Fetch bin details
        const binResponse = await axiosInstance.get('/get_bins');
        const validBins = binIdentifiers
          .map(identifier => binResponse.data.find(bin => bin.unique_identifier === identifier))
          .filter(bin => bin);

        if (validBins.length > 0) {
          const binIds = validBins.map(bin => bin.id).join(',');
          let timePeriod = dayvalue.toLowerCase().replace(/\s+/g, '');
          
          const wasteTypes = ['organic', 'paper', 'pmd', 'residual', 'rest'];
          let totalObjects = 0;
          
          // Fetch objects for all waste types
          const wastePromises = wasteTypes.map(wasteType => {
            const url = `/wastebarchart/${binIds}/${timePeriod}/`;
            const params = { wasteitem: wasteType };
            
            // Add custom date range if applicable - SAFE HANDLING
            if (hasCustomDates) {
              const startISO = toISOString(datevalue[0].startDate);
              const endISO = toISOString(datevalue[0].endDate);
              
              if (startISO && endISO) {
                params.start_date = startISO;
                params.end_date = endISO;
              }
            }
            
            return axiosInstance.get(url, { params }).catch(err => {
              console.warn(`Failed to fetch ${wasteType}:`, err.message);
              return { data: { objects: 0 } };
            });
          });
          
          const wasteResponses = await Promise.all(wastePromises);
          
          wasteResponses.forEach((response, index) => {
            if (response.data && response.data.objects !== undefined) {
              totalObjects += response.data.objects;
              console.log(`${wasteTypes[index]} objects:`, response.data.objects);
            }
          });
          
          console.log(`Total objects across all waste types:`, totalObjects);
          setTotalWasteProducedData(totalObjects);
          
          // Fetch percentage change and color for the main waste type
          try {
            const mainParams = { wasteitem: currentWasteItem_ };
            
            if (hasCustomDates) {
              const startISO = toISOString(datevalue[0].startDate);
              const endISO = toISOString(datevalue[0].endDate);
              
              if (startISO && endISO) {
                mainParams.start_date = startISO;
                mainParams.end_date = endISO;
              }
            }
            
            const mainWasteResponse = await axiosInstance.get(
              `/wastebarchart/${binIds}/${timePeriod}/`,
              { params: mainParams }
            );
            
            if (mainWasteResponse.data) {
              const percentageChange = parseFloat(mainWasteResponse.data?.percentage_change || 0);
              setPercentageChangeWasteChart(percentageChange.toFixed(2));
              setColorWasteChart(mainWasteResponse.data?.color || 'gray');
              
              console.log('Percentage change data:', {
                percentage_change: percentageChange,
                color: mainWasteResponse.data?.color
              });
            }
          } catch (mainError) {
            console.warn('Failed to fetch main waste type stats:', mainError);
            setPercentageChangeWasteChart(0);
            setColorWasteChart('gray');
          }
        }

      } catch (error) {
        console.error('SubclassDisplay fetch error:', error);
        setPercentageChangeWasteChart(0);
        setColorWasteChart('gray');
        setTotalWasteProducedData(0);
        
        if (wasteweightProp !== undefined && wasteweightProp !== null) {
          setWasteWeight(Number(wasteweightProp.toFixed(2)));
        } else {
          setWasteWeight(0);
        }
      }
    };

    fetchData();
  }, [currentWasteItem_, id, dayvalue, wasteweightProp, isCustomDateRange, datevalue]);

  return (
    <SubclassDisplayChart
      id={Array.isArray(id) ? id.join(',') : id}
      wasteitem={currentWasteItem}
      dayvalue={dayvalue}
      datevalue={datevalue}
      selectedButton={selectedButton}
      totalwasteproduceddata={totalwasteproduceddata}
      wasteweight={wasteweight}
      colorwastechart={colorwastechart}
      percentagechangewastechart={percentagechangewastechart}
      season={season}
      isCustomDateRange={isCustomDateRange}
    />
  );
};

export default SubclassDisplay;